import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Button, Form, Spinner, Alert } from "react-bootstrap";
import { DateTime } from "luxon";
import Place, { Place_Source } from "../../Models/Place";
import Appointment from "../../Models/Event";
import {
  searchTrails,
  fetchTrailGeometry,
  segmentsToPoints,
  uniqueGroupName,
  formatDuration,
  networkInfo,
} from "../../Models/OverpassTrails";

const ROUTE_TYPE_OPTIONS = [
  { value: "foot",    label: "Walking" },
  { value: "hiking",  label: "Hiking" },
  { value: "bicycle", label: "Cycling" },
  { value: "mtb",     label: "MTB" },
];

const ALL_TYPES = new Set(ROUTE_TYPE_OPTIONS.map((o) => o.value));

function matchesType(routeType, activeTypes) {
  if (activeTypes.size === 0 || activeTypes.size === ALL_TYPES.size) return true;
  const t = (routeType || "").toLowerCase();
  for (const type of activeTypes) {
    if (t.includes(type)) return true;
  }
  return false;
}

function routeLabel(type) {
  if (!type) return "Walk";
  const t = type.toLowerCase();
  if (t.includes("mtb")) return "MTB";
  if (t.includes("bicycle")) return "Cycle";
  if (t.includes("hiking")) return "Hike";
  return "Walk";
}

function routeBadgeStyle(type) {
  const label = routeLabel(type);
  const colors = {
    Hike:  { background: "#d4edda", color: "#155724" },
    Cycle: { background: "#cce5ff", color: "#004085" },
    MTB:   { background: "#fff3cd", color: "#856404" },
    Walk:  { background: "#e2e3e5", color: "#383d41" },
  };
  return colors[label] || colors.Walk;
}

export default function TrailSearchModal({ show, onHide, mapBounds, onPreview, onAdd }) {
  const isMobile = useSelector((state) => state.MobileMode);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [allResults,    setAllResults]    = useState([]);
  const [activeTypes,   setActiveTypes]   = useState(new Set(ALL_TYPES));
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [addToSchedule, setAddToSchedule] = useState(false);
  const [scheduleDate,  setScheduleDate]  = useState("");
  const [startHour,     setStartHour]     = useState(9);
  const [previewedId,   setPreviewedId]   = useState(null);
  const [loadingGeoId,  setLoadingGeoId]  = useState(null);
  const [adding,        setAdding]        = useState(false);
  const [minKm,         setMinKm]         = useState("");
  const [maxKm,         setMaxKm]         = useState("");
  const [sortBy,        setSortBy]        = useState("distance");
  const [searchTick,    setSearchTick]    = useState(0);

  const geoCache  = useRef({});
  const closedRef = useRef(false);

  const visibleResults = (() => {
    const filtered = allResults.filter((t) => {
      if (!matchesType(t.routeType, activeTypes)) return false;
      if (t.distanceKm != null) {
        if (minKm !== "" && t.distanceKm < parseFloat(minKm)) return false;
        if (maxKm !== "" && t.distanceKm > parseFloat(maxKm)) return false;
      }
      return true;
    });
    if (sortBy === "distance") {
      filtered.sort((a, b) => {
        if (a.distanceKm == null && b.distanceKm == null) return 0;
        if (a.distanceKm == null) return 1;
        if (b.distanceKm == null) return -1;
        return a.distanceKm - b.distanceKm;
      });
    } else if (sortBy === "name") {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }
    return filtered;
  })();

  useEffect(() => {
    if (!show) return;
    try {
      const firstDate = window.guide_obj?.schedule?.getFirsEventStartDate("Main");
      if (firstDate && firstDate.isValid) {
        setScheduleDate(firstDate.toFormat("yyyy-MM-dd"));
        return;
      }
    } catch (_) {}
    setScheduleDate(DateTime.utc().toFormat("yyyy-MM-dd"));
  }, [show]);

  useEffect(() => {
    if (!show || !mapBounds) return;
    let cancelled = false;

    closedRef.current = false;
    setLoading(true);
    setError(null);
    setAllResults([]);
    setSelectedIds(new Set());
    setPreviewedId(null);
    onPreview(null);
    geoCache.current = {};

    searchTrails(mapBounds)
      .then((trails) => { if (!cancelled) setAllResults(trails); })
      .catch((e)     => { if (!cancelled) setError(e.message || "Failed to fetch trails"); })
      .finally(()    => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [show, searchTick]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleType = (value) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      next.has(value) ? next.delete(value) : next.add(value);
      return next;
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleMouseEnter = useCallback(async (trail) => {
    setPreviewedId(trail.id);
    let geo = geoCache.current[trail.id];
    if (!geo) {
      setLoadingGeoId(trail.id);
      geo = await fetchTrailGeometry(trail.id).catch(() => null);
      setLoadingGeoId(null);
      if (geo) geoCache.current[trail.id] = geo;
    }
    // Panel may have closed while the fetch was in flight
    if (closedRef.current) return;
    if (geo) onPreview(geo.segments);
    else console.warn("[TrailSearchModal] no geometry for", trail.id, trail.name);
  }, [onPreview]);

  const handleMouseLeave = useCallback(() => {
    setPreviewedId(null);
    setLoadingGeoId(null);
    onPreview(null);
  }, [onPreview]);

  // Mobile mode has no hover, so tapping a row (mirrors the mobile app's
  // tap-to-expand) doubles as the preview trigger instead — tap again (or
  // the "tap to show list" bar) to clear it. Checkbox has its own hit area
  // (stopPropagation below) so this doesn't fight with selecting a trail.
  const handleRowTap = useCallback((trail) => {
    if (previewedId === trail.id) handleMouseLeave();
    else handleMouseEnter(trail);
  }, [previewedId, handleMouseEnter, handleMouseLeave]);

  const handleHide = () => {
    closedRef.current = true;
    onPreview(null);
    setPreviewedId(null);
    onHide();
  };

  const handleAdd = async () => {
    const toAdd = visibleResults.filter((t) => selectedIds.has(t.id));
    if (toAdd.length === 0) return;
    setAdding(true);

    const existingGroupNames = (window.guide_obj?.groups || []).map((g) => g.name);

    for (const trail of toAdd) {
      let geo = geoCache.current[trail.id];
      if (!geo) geo = await fetchTrailGeometry(trail.id).catch(() => null);
      if (!geo) {
        console.warn("[TrailSearchModal] skipping trail, no geometry:", trail.name);
        continue;
      }
      geoCache.current[trail.id] = geo;

      const groupName = uniqueGroupName(trail.name, existingGroupNames);
      existingGroupNames.push(groupName);
      const groupId = window.guide_obj.addGroup(groupName);

      const pathPlace = new Place(null);
      pathPlace.Name   = trail.name;
      pathPlace.PType  = "PolyLine";
      pathPlace.Points = segmentsToPoints(geo.segments);
      pathPlace.Group  = [groupId];
      pathPlace.Source = Place_Source.USER;
      if (trail.description) pathPlace.Info = `<p>${trail.description}</p>`;
      window.guide_obj.places.addPlace(pathPlace);

      const fp = geo.firstPt;
      if (fp && typeof fp.lat === "number" && typeof fp.lon === "number") {
        const trailhead = new Place(null);
        trailhead.Name   = `${trail.name} (trailhead)`;
        trailhead.PType  = "Icon";
        trailhead.Lat    = fp.lat.toString();
        trailhead.Longi  = fp.lon.toString();
        trailhead.Group  = [groupId];
        trailhead.Source = Place_Source.USER;
        window.guide_obj.places.addPlace(trailhead);
      }

      if (addToSchedule && trail.durationHours != null && scheduleDate) {
        try {
          const startDT = DateTime.fromISO(
            `${scheduleDate}T${String(startHour).padStart(2, "0")}:00:00`,
            { zone: "utc" }
          );
          const event = new Appointment(window.guide_obj.schedule);
          event.Subject   = trail.name;
          event.Body      = trail.description || "";
          event.Start     = startDT.toISO();
          event.End       = startDT.plus({ hours: trail.durationHours }).toISO();
          event.Places    = pathPlace.ID;
          event.Itinerary = "Main";
          window.guide_obj.schedule.addEvent(event);
        } catch (_) {}
      }
    }

    // Compute bounds of all added trails before closing
    let fitBounds = null;
    if (window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      let hasPoints = false;
      for (const trail of toAdd) {
        const geo = geoCache.current[trail.id];
        if (!geo) continue;
        for (const seg of geo.segments) {
          for (const pt of seg) {
            if (typeof pt.lat === "number" && typeof pt.lon === "number") {
              bounds.extend({ lat: pt.lat, lng: pt.lon });
              hasPoints = true;
            }
          }
        }
      }
      if (hasPoints) fitBounds = bounds;
    }

    setAdding(false);
    closedRef.current = true;
    onPreview(null);
    onAdd();   // triggers Redux update which resets MapCenter
    onHide();

    // Defer fitBounds until after the Redux re-render resets the viewport
    if (fitBounds && window.mapRef) {
      setTimeout(() => window.mapRef.fitBounds(fitBounds), 150);
    }
  };

  if (!show) return null;

  const noneFound = !loading && !error && allResults.length === 0;
  const canAdd    = selectedIds.size > 0 && !adding;
  const previewedTrail = isMobile ? allResults.find((t) => t.id === previewedId) : null;
  const isPreviewingMobile = !!previewedTrail;

  const panelStyle = isMobile ? {
    position: "fixed",
    left: 0, right: 0, bottom: 0,
    // 55vh left too little room for the scrollable results list once the
    // header, two filter rows, and footer (schedule toggle + action buttons)
    // — all fixed-size siblings of the flex:1 results div — took their
    // share on small phone screens. Mirrors the same fix made to the mobile
    // app's TrailSearch.js. Found/fixed 2026-09-09.
    //
    // But at 85vh the map (and the previewed route line) is mostly hidden,
    // so while a trail is being previewed the panel shrinks to a small peek
    // strip instead, revealing the map — same tradeoff/fix as the mobile
    // app's TrailSearch.js peek state.
    height: isPreviewingMobile ? "auto" : "85vh",
    maxHeight: isPreviewingMobile ? "30vh" : "85vh",
    transition: "height 0.2s ease, max-height 0.2s ease",
    borderRadius: "12px 12px 0 0",
    boxShadow: "0 -4px 16px rgba(0,0,0,0.18)",
    borderTop: "1px solid #dee2e6",
  } : {
    position: "fixed",
    top: 56, left: 0, bottom: 0,
    width: 320,
    boxShadow: "2px 0 12px rgba(0,0,0,0.18)",
    borderRight: "1px solid #dee2e6",
  };

  return (
    <div style={{
      ...panelStyle,
      background: "white",
      display: "flex",
      flexDirection: "column",
      zIndex: 1040,
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px",
        borderBottom: "1px solid #dee2e6",
        flexShrink: 0,
      }}>
        <strong style={{ fontSize: "0.95rem" }}>
          {isPreviewingMobile ? previewedTrail.name : "Find Trails & Paths"}
        </strong>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <button
            onClick={() => setSearchTick((n) => n + 1)}
            disabled={loading}
            style={{
              fontSize: "0.78rem", fontWeight: 600,
              padding: "3px 10px", borderRadius: 4,
              border: "1px solid #0d6efd",
              background: "#0d6efd", color: "white",
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.65 : 1,
            }}
          >
            {loading ? "Searching…" : "Search"}
          </button>
          <button
            onClick={handleHide}
            style={{
              background: "none", border: "none", fontSize: "1.2rem",
              lineHeight: 1, cursor: "pointer", color: "#666", padding: "2px 6px",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
      </div>

      {isPreviewingMobile ? (
        <div
          onClick={handleMouseLeave}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
            padding: "12px 14px",
            fontSize: "0.85rem", fontWeight: 600, color: "#0d6efd",
            cursor: "pointer",
          }}
        >
          ▲ Previewing on map — tap to show list
        </div>
      ) : (
      <>
      {/* Filter pills */}
      <div style={{
        display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
        padding: "8px 14px",
        borderBottom: "1px solid #f0f0f0",
        flexShrink: 0,
      }}>
        {ROUTE_TYPE_OPTIONS.map(({ value, label }) => {
          const active = activeTypes.has(value);
          return (
            <button
              key={value}
              onClick={() => toggleType(value)}
              style={{
                padding: "2px 10px",
                borderRadius: 20,
                border: `1px solid ${active ? "#0d6efd" : "#dee2e6"}`,
                background: active ? "#0d6efd" : "white",
                color: active ? "white" : "#444",
                fontSize: "0.75rem",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          );
        })}
        {loading && (
          <span style={{ marginLeft: "auto", fontSize: "0.75rem", color: "#6c757d", display: "flex", alignItems: "center", gap: 5 }}>
            <Spinner animation="border" size="sm" /> Searching…
          </span>
        )}
      </div>

      {/* Distance + sort filter */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderBottom: "1px solid #f0f0f0", flexShrink: 0, flexWrap: "wrap" }}>
        <span style={{ fontSize: "0.75rem", color: "#666", flexShrink: 0 }}>km:</span>
        <input
          type="number" min="0" placeholder="Min"
          value={minKm}
          onChange={(e) => setMinKm(e.target.value)}
          style={{ width: 54, fontSize: "0.78rem", padding: "2px 5px", border: "1px solid #dee2e6", borderRadius: 4 }}
        />
        <span style={{ fontSize: "0.75rem", color: "#999" }}>–</span>
        <input
          type="number" min="0" placeholder="Max"
          value={maxKm}
          onChange={(e) => setMaxKm(e.target.value)}
          style={{ width: 54, fontSize: "0.78rem", padding: "2px 5px", border: "1px solid #dee2e6", borderRadius: 4 }}
        />
        {(minKm !== "" || maxKm !== "") && (
          <button
            onClick={() => { setMinKm(""); setMaxKm(""); }}
            style={{ fontSize: "0.72rem", color: "#888", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            clear
          </button>
        )}
        <span style={{ fontSize: "0.75rem", color: "#666", marginLeft: "auto", flexShrink: 0 }}>Sort:</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{ fontSize: "0.75rem", padding: "2px 4px", border: "1px solid #dee2e6", borderRadius: 4, background: "white" }}
        >
          <option value="distance">Distance</option>
          <option value="name">Name</option>
        </select>
      </div>

      {/* Results list — scrollable */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
        {error && <Alert variant="danger" style={{ fontSize: "0.82rem" }}>{error}</Alert>}

        {noneFound && (
          <div style={{ textAlign: "center", color: "#6c757d", padding: "24px 8px", fontSize: "0.85rem" }}>
            No named trails found in this area.
            <br />
            <span style={{ fontSize: "0.78rem" }}>Try zooming out or panning the map.</span>
          </div>
        )}

        {!loading && allResults.length > 0 && visibleResults.length === 0 && (
          <div style={{ textAlign: "center", color: "#6c757d", padding: "24px 8px", fontSize: "0.85rem" }}>
            No results match the selected filters.
          </div>
        )}

        {visibleResults.length > 0 && (
          <>
            <div style={{ fontSize: "0.75rem", color: "#888", marginBottom: 6 }}>
              {visibleResults.length} route{visibleResults.length !== 1 ? "s" : ""} — hover to preview, check to add
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {visibleResults.map((trail) => {
                const isPreviewed = previewedId === trail.id;
                const isSelected  = selectedIds.has(trail.id);
                return (
                  <div
                    key={trail.id}
                    onMouseEnter={isMobile ? undefined : () => handleMouseEnter(trail)}
                    onMouseLeave={isMobile ? undefined : handleMouseLeave}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                      padding: "6px 8px",
                      borderRadius: 6,
                      border: `1px solid ${isPreviewed ? "#0d6efd" : "#dee2e6"}`,
                      background: isPreviewed ? "#f0f5ff" : isSelected ? "#f8f9fa" : "white",
                      cursor: "pointer",
                      transition: "border-color 0.1s, background 0.1s",
                    }}
                    onClick={() => (isMobile ? handleRowTap(trail) : toggleSelected(trail.id))}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelected(trail.id)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ width: 16, height: 16, flexShrink: 0, flexGrow: 0, cursor: "pointer", marginTop: 2 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Row 1: name (truncated) + loading spinner */}
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{
                          fontWeight: 600, fontSize: "0.82rem",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          flex: 1, minWidth: 0,
                        }}>
                          {trail.name}
                        </span>
                        {loadingGeoId === trail.id && (
                          <Spinner animation="border" size="sm" style={{ width: 11, height: 11, borderWidth: 2, flexShrink: 0 }} />
                        )}
                      </div>
                      {/* Row 2: badge + stats */}
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2, flexWrap: "wrap" }}>
                        <span style={{
                          ...routeBadgeStyle(trail.routeType),
                          fontSize: "0.65rem", fontWeight: 700,
                          padding: "0px 4px", borderRadius: 3, flexShrink: 0,
                        }}>
                          {routeLabel(trail.routeType)}
                        </span>
                        {(() => {
                          const ni = networkInfo(trail.network);
                          return ni ? (
                            <span style={{
                              fontSize: "0.65rem", fontWeight: 700,
                              padding: "0px 4px", borderRadius: 3, flexShrink: 0,
                              background: ni.color + "22", color: ni.color,
                              border: `1px solid ${ni.color}55`,
                            }}>
                              {ni.label}
                            </span>
                          ) : null;
                        })()}
                        {trail.roundtrip === "yes" && (
                          <span title="Loop / roundtrip" style={{ fontSize: "0.72rem", color: "#555", flexShrink: 0 }}>↻</span>
                        )}
                        {trail.roundtrip === "no" && (
                          <span title="Point-to-point" style={{ fontSize: "0.72rem", color: "#555", flexShrink: 0 }}>→</span>
                        )}
                        {trail.distanceKm != null && (
                          <span style={{ fontSize: "0.72rem", color: "#666" }}>{trail.distanceKm} km</span>
                        )}
                        {trail.durationHours != null && (
                          <span style={{ fontSize: "0.72rem", color: "#666" }}>~{formatDuration(trail.durationHours)}</span>
                        )}
                        {trail.wikipedia && (() => {
                          const tag = trail.wikipedia;
                          const m = tag.match(/^([a-z]{2,3}):(.*)/);
                          const lang  = m ? m[1] : "en";
                          const title = m ? m[2] : tag;
                          const url = `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, "_"))}`;
                          return (
                            <a
                              href={url}
                              target="_blank" rel="noreferrer"
                              style={{ fontSize: "0.68rem", color: "#0d6efd" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Wiki
                            </a>
                          );
                        })()}
                        {trail.website && (
                          <a
                            href={trail.website}
                            target="_blank" rel="noreferrer"
                            style={{ fontSize: "0.68rem", color: "#0d6efd" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Website
                          </a>
                        )}
                      </div>
                      {/* Description: shown when hovered or selected */}
                      {(isPreviewed || isSelected) && trail.description && (
                        <div style={{
                          marginTop: 4, fontSize: "0.73rem", color: "#555",
                          lineHeight: 1.4, borderTop: "1px solid #e9ecef", paddingTop: 4,
                        }}>
                          {trail.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {allResults.length > 0 && (
        <div style={{
          borderTop: "1px solid #dee2e6",
          padding: "10px 14px",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={addToSchedule}
                onChange={(e) => setAddToSchedule(e.target.checked)}
              />
              Add to schedule
            </label>
            {addToSchedule && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4, width: "100%" }}>
                <Form.Control
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  style={{ flex: 1, fontSize: "0.8rem" }}
                />
                <Form.Select
                  value={startHour}
                  onChange={(e) => setStartHour(parseInt(e.target.value))}
                  style={{ width: 80, fontSize: "0.8rem" }}
                >
                  {Array.from({ length: 14 }, (_, i) => i + 6).map((h) => (
                    <option key={h} value={h}>{h}:00</option>
                  ))}
                </Form.Select>
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <Button variant="outline-secondary" size="sm" onClick={handleHide} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleAdd} disabled={!canAdd} style={{ flex: 2 }}>
              {adding
                ? <><Spinner animation="border" size="sm" className="me-1" />Adding…</>
                : `Add${selectedIds.size > 0 ? ` ${selectedIds.size}` : ""} Selected`}
            </Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
